// PreventiApp · AdminHome — Portada del panel con diferenciación clara por rol.
// Sprint 5 · Paso 4 · Diferenciación visual (sin cambios de permisos ni RLS).
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Building2, UserCog, GraduationCap, ClipboardList,
  LifeBuoy, FileUp, Smartphone, LayoutDashboard, BookOpen,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const ROLE_META = {
  super_admin_inside: {
    label: 'Admin Supra INSIDE',
    color: '#5346E6',
    bg: '#EEF4FF',
    puede: [
      'Ver el dashboard preventivo de cualquier colegio del piloto.',
      'Gestionar colegios, usuarios y auditoría global.',
      'Autorizar accesos temporales de soporte.',
    ],
    noPuede: [],
  },
  admin_colegio: {
    label: 'Admin de Colegio',
    color: '#0F6C4B',
    bg: '#E7F5EE',
    puede: [
      'Ver el dashboard preventivo de tu colegio.',
      'Gestionar usuarios, cursos y observaciones del colegio.',
      'Asignar responsables y cerrar casos.',
    ],
    noPuede: ['Ver o modificar información de otros colegios.'],
  },
  directivo: {
    label: 'Directivo',
    color: '#7A2AB1',
    bg: '#F3E6FA',
    puede: [
      'Ver el dashboard preventivo de tu colegio.',
      'Asignar responsables y cambiar el estado de un caso.',
      'Cerrar casos con justificación.',
    ],
    noPuede: ['Gestionar colegios, cursos o usuarios (eso lo hace el Admin de Colegio).'],
  },
  encargado_convivencia: {
    label: 'Encargado de Convivencia',
    color: '#B97A0D',
    bg: '#FFF1D6',
    puede: [
      'Ver el dashboard preventivo de tu colegio.',
      'Asignar responsables y llevar el seguimiento diario de los casos.',
      'Registrar acciones, próximas revisiones y cerrar casos.',
    ],
    noPuede: ['Gestionar colegios, cursos o usuarios.'],
  },
  orientador: {
    label: 'Orientador',
    color: '#0E5FB9',
    bg: '#E4F0FB',
    puede: [
      'Ver las observaciones y casos del carril clínico y aquellos donde eres responsable.',
      'Registrar seguimientos en los casos que tienes asignados.',
    ],
    noPuede: [
      'Ver todos los casos del colegio (solo los del carril clínico y los asignados a ti).',
      'Asignar responsables.',
    ],
  },
  docente: {
    label: 'Docente',
    color: '#475472',
    bg: '#F4F7FE',
    puede: ['Registrar observaciones en terreno desde la app móvil.'],
    noPuede: ['Acceder al dashboard preventivo institucional.'],
  },
  inspector: {
    label: 'Inspector',
    color: '#475472',
    bg: '#F4F7FE',
    puede: ['Registrar observaciones en terreno desde la app móvil.'],
    noPuede: ['Acceder al dashboard preventivo institucional.'],
  },
};

const Tile = ({ to, icon: Icon, title, desc, badge, badgeColor = '#5346E6', testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group block rounded-2xl border border-[#E5EAF5] bg-white p-5 hover:border-[#5346E6] hover:shadow-md transition"
  >
    <div className="flex items-start justify-between mb-2">
      <div className="w-10 h-10 rounded-xl bg-[#F4F7FE] flex items-center justify-center group-hover:bg-[#EEF4FF] transition">
        <Icon className="w-5 h-5 text-[#5346E6]" />
      </div>
      {badge && (
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: badgeColor + '15', color: badgeColor }}
          data-testid={`${testid}-badge`}
        >
          {badge}
        </span>
      )}
    </div>
    <p className="text-sm font-extrabold text-[#0B1B5E]">{title}</p>
    <p className="mt-1 text-xs text-[#475472]">{desc}</p>
  </Link>
);

const RoleBanner = ({ meta, colegioNombre }) => (
  <div
    className="rounded-2xl border p-4 mb-6"
    style={{ background: meta.bg, borderColor: meta.color + '30' }}
    data-testid="role-banner"
  >
    <div className="flex items-center gap-2 mb-2">
      <ShieldCheck className="w-4 h-4" style={{ color: meta.color }} />
      <span
        className="text-xs font-extrabold uppercase tracking-wide"
        style={{ color: meta.color }}
        data-testid="role-banner-label"
      >
        {meta.label}
      </span>
      {colegioNombre && (
        <span className="text-xs text-[#475472]">· {colegioNombre}</span>
      )}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
      <div>
        <p className="font-bold text-[#0B1B5E] mb-1">Con este rol puedes</p>
        <ul className="space-y-1 text-[#475472]">
          {meta.puede.map((p, i) => (
            <li key={i} className="flex gap-1.5">
              <span style={{ color: meta.color }}>•</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
      {meta.noPuede.length > 0 && (
        <div>
          <p className="font-bold text-[#0B1B5E] mb-1">No corresponde a tu rol</p>
          <ul className="space-y-1 text-[#475472]">
            {meta.noPuede.map((p, i) => (
              <li key={i} className="flex gap-1.5">
                <span className="text-[#94A3B8]">·</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
);

export default function AdminHome() {
  const { profile } = useAuth();
  const rol = profile?.rol;
  const perms = profile?.permisos_inside || {};
  const isInside = profile?.es_inside_staff === true;
  const isAdminColegio = rol === 'admin_colegio';
  const isDirectivo = rol === 'directivo';
  const isEncargado = rol === 'encargado_convivencia';
  const isOrientador = rol === 'orientador';
  const isMobile = ['docente', 'inspector'].includes(rol);
  const meta = ROLE_META[rol] || ROLE_META.docente;

  const primerNombre = profile?.nombre_completo?.split(' ')[0] || 'usuario';

  return (
    <div className="p-8 max-w-5xl" data-testid="admin-home">
      <div className="mb-6">
        <h1
          className="text-3xl font-extrabold text-[#0B1B5E]"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          data-testid="admin-home-greeting"
        >
          Hola, {primerNombre}
        </h1>
        <p className="mt-1 text-sm text-[#475472]">
          Ingresaste a PreventiApp {isInside ? 'con permisos INSIDE' : 'como parte del equipo de tu colegio'}.
        </p>
      </div>

      <RoleBanner meta={meta} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Dashboard preventivo — 4 roles con acceso pleno */}
        {(isInside || isAdminColegio || isDirectivo || isEncargado) && (
          <Tile
            to="/admin/dashboard"
            icon={LayoutDashboard}
            title="Dashboard preventivo"
            desc="Matriz preventiva, indicadores navegables y explorador de casos."
            badge="Operativo diario"
            badgeColor="#5346E6"
            testid="tile-dashboard"
          />
        )}

        {/* Orientador: dashboard limitado (backend filtra por carril y asignación) */}
        {isOrientador && (
          <Tile
            to="/admin/dashboard"
            icon={LayoutDashboard}
            title="Dashboard clínico"
            desc="Sólo se muestran las observaciones del carril clínico y los casos que tengas asignados."
            badge="Vista limitada"
            badgeColor="#0E5FB9"
            testid="tile-dashboard"
          />
        )}

        {/* INSIDE: gestión de colegios */}
        {isInside && (rol === 'super_admin_inside' || perms.puede_gestionar_colegios) && (
          <Tile
            to="/admin/colegios"
            icon={Building2}
            title="Colegios"
            desc="Listar, editar y dar de alta colegios del piloto."
            badge="Gestión INSIDE"
            badgeColor="#5346E6"
            testid="tile-colegios"
          />
        )}

        {/* Usuarios: INSIDE + Admin Colegio */}
        {(isInside || isAdminColegio) && (
          <Tile
            to="/admin/usuarios"
            icon={UserCog}
            title="Usuarios"
            desc={isAdminColegio
              ? 'Crear, editar y activar perfiles de tu colegio.'
              : 'Crear, editar, activar/desactivar perfiles y resetear contraseñas.'}
            badge="Gestión"
            badgeColor={isAdminColegio ? '#0F6C4B' : '#5346E6'}
            testid="tile-usuarios"
          />
        )}

        {/* Cursos: solo Admin Colegio */}
        {isAdminColegio && (
          <Tile
            to="/admin/cursos"
            icon={GraduationCap}
            title="Cursos"
            desc="Listar y dar de alta los cursos del colegio."
            badge="Gestión"
            badgeColor="#0F6C4B"
            testid="tile-cursos"
          />
        )}

        {/* Observaciones: para revisión */}
        {(isInside || isAdminColegio || isDirectivo || isEncargado || isOrientador) && (
          <Tile
            to="/admin/observaciones"
            icon={ClipboardList}
            title="Observaciones"
            desc="Lista filtrable de las observaciones registradas según tu rol."
            badge="Lectura"
            badgeColor="#475472"
            testid="tile-observaciones"
          />
        )}

        {/* Soporte INSIDE: para todos, pero con matiz según rol */}
        <Tile
          to="/admin/soporte"
          icon={LifeBuoy}
          title="Soporte INSIDE"
          desc={isInside
            ? 'Autorizar / revocar acceso temporal de soporte.'
            : 'Solicita ayuda al equipo INSIDE cuando lo necesites.'}
          badge={isInside ? 'Gestión INSIDE' : 'Contacto'}
          badgeColor={isInside ? '#5346E6' : '#475472'}
          testid="tile-soporte"
        />

        {/* Carga masiva: solo INSIDE con permiso */}
        {isInside && (rol === 'super_admin_inside' || perms.puede_gestionar_usuarios) && (
          <Tile
            to="/admin/import"
            icon={FileUp}
            title="Carga masiva"
            desc="Plantillas xlsx/csv para alta de colegios y usuarios."
            badge="Gestión INSIDE"
            badgeColor="#5346E6"
            testid="tile-import"
          />
        )}

        {/* Móviles: docente/inspector solo pueden ir a la app móvil */}
        {isMobile && (
          <Tile
            to="/"
            icon={Smartphone}
            title="App móvil"
            desc="Registrar observaciones en terreno desde tu teléfono."
            badge="Terreno"
            badgeColor="#475472"
            testid="tile-mobile"
          />
        )}

        {/* Criterios preventivos — link a documentación */}
        {(isInside || isAdminColegio || isDirectivo || isEncargado || isOrientador) && (
          <Tile
            to="/admin/dashboard"
            icon={BookOpen}
            title="Criterios preventivos"
            desc="Cómo se calcula el nivel Verde / Amarillo / Rojo y cómo se activan los eventos centinela."
            badge="Referencia"
            badgeColor="#475472"
            testid="tile-criterios"
          />
        )}
      </div>
    </div>
  );
}
